import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ScribePanel from '../components/scribe/ScribePanel';
import NoteReview from '../components/scribe/NoteReview';
import { formatApptTime } from '../utils/apptTime';

const STATUS_COLORS = {
  scheduled:    { bg: '#dbeafe', text: '#1e40af', label: 'Scheduled' },
  'checked-in': { bg: '#d1fae5', text: '#065f46', label: 'Checked In' },
  completed:    { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  'no-show':    { bg: '#fef3c7', text: '#92400e', label: 'No-Show' },
  canceled:     { bg: '#fee2e2', text: '#991b1b', label: 'Canceled' },
  rescheduled:  { bg: '#e0e7ff', text: '#3730a3', label: 'Rescheduled' }
};

function AppointmentScribe({ practiceId }) {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({ specialties: [], noteTemplates: [] });

  // Scribe session state
  const [scribeSession, setScribeSession] = useState(null);
  const [sessionNotes, setSessionNotes] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  // Whether we're in active scribe mode (ScribePanel visible)
  const [activeScribe, setActiveScribe] = useState(false);

  const loadData = useCallback(async () => {
    if (!practiceId || !appointmentId) return;
    setLoading(true);
    setError(null);
    try {
      // Load appointment, config, and scribe sessions in parallel
      const [apptRes, configRes, sessionsRes] = await Promise.all([
        api.getAppointment(practiceId, appointmentId),
        api.getScribeConfig(practiceId),
        api.getScribeSessions(practiceId, { appointmentId })
      ]);

      setAppointment(apptRes.data || null);
      if (configRes.data) setConfig(configRes.data);

      // Check for existing scribe session
      const sessions = sessionsRes.data || [];
      if (sessions.length > 0) {
        const session = sessions[0]; // latest
        setScribeSession(session);
        setSessionNotes(null);

        // If session completed, load notes
        if (session.status === 'completed' && session.note_status !== 'pending') {
          setNotesLoading(true);
          try {
            const notesRes = await api.getScribeNotes(practiceId, session.guid);
            setSessionNotes(notesRes.data || null);
          } catch (err) {
            console.error('Failed to load notes:', err);
          } finally {
            setNotesLoading(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load appointment:', err);
      setError(err.message || 'Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [practiceId, appointmentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGoBack = () => {
    navigate('/practice-view/ambient-scribe');
  };

  const handleStartScribe = () => {
    setActiveScribe(true);
  };

  const handleSessionComplete = async (sessionGuid) => {
    setActiveScribe(false);
    // Reload data to get the completed session and notes
    await loadData();
  };

  const handleScribeBack = () => {
    setActiveScribe(false);
  };

  const handleSaveNotes = async (sections) => {
    if (!scribeSession) return;
    try {
      setNotesSaving(true);
      await api.updateScribeNotes(practiceId, scribeSession.guid, sections);
      const res = await api.getScribeNotes(practiceId, scribeSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to save notes: ' + err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  const handleApproveNotes = async () => {
    if (!scribeSession) return;
    if (!window.confirm('Approve these notes? This action locks the notes for clinical use.')) return;
    try {
      await api.approveScribeNotes(practiceId, scribeSession.guid);
      const res = await api.getScribeNotes(practiceId, scribeSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to approve notes: ' + err.message);
    }
  };

  const handleRejectNotes = async () => {
    if (!scribeSession) return;
    if (!window.confirm('Reject these notes? You can re-edit and resubmit later.')) return;
    try {
      await api.rejectScribeNotes(practiceId, scribeSession.guid);
      const res = await api.getScribeNotes(practiceId, scribeSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to reject notes: ' + err.message);
    }
  };

  const handlePushToEHR = async () => {
    if (!scribeSession) return;
    if (!window.confirm('Push these notes to the EHR? This will update the encounter assessment.')) return;
    try {
      // Optimistically update push status in UI
      setSessionNotes(prev => prev ? { ...prev, ehrPushStatus: 'pushing' } : prev);
      await api.pushScribeNotesToEHR(practiceId, scribeSession.guid);
      // Reload to get updated status
      const res = await api.getScribeNotes(practiceId, scribeSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      // Reload to get the failed status from server
      try {
        const res = await api.getScribeNotes(practiceId, scribeSession.guid);
        setSessionNotes(res.data || null);
      } catch (_) { /* ignore reload error */ }
      alert('Failed to push notes to EHR: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.loading}>Loading appointment...</div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.errorCard}>
          <h3 style={styles.errorTitle}>Appointment Not Found</h3>
          <p style={styles.errorText}>{error || 'The appointment could not be loaded.'}</p>
          <button style={styles.backBtn} onClick={handleGoBack}>
            <BackIcon /> Back to Appointments
          </button>
        </div>
      </div>
    );
  }

  // If actively recording with ScribePanel
  if (activeScribe) {
    return (
      <div style={styles.pageContainer}>
        <ScribePanel
          practiceId={practiceId}
          patient={{
            id: appointment.patient_id,
            first_name: appointment.first_name,
            last_name: appointment.last_name
          }}
          appointmentId={appointment.id}
          specialties={config.specialties}
          noteTemplates={config.noteTemplates}
          onSessionComplete={handleSessionComplete}
          onBack={handleScribeBack}
        />
      </div>
    );
  }

  const isCheckedIn = appointment.status === 'checked-in';
  const hasCompletedScribe = scribeSession && scribeSession.status === 'completed';
  const hasActiveScribe = scribeSession && ['initializing', 'streaming', 'paused', 'processing'].includes(scribeSession.status);
  // Failed/cancelled sessions rendered nothing at all here (GH-184: the
  // HealthScribe permission error sat in error_message for five months).
  const hasFailedScribe = scribeSession && ['failed', 'cancelled'].includes(scribeSession.status);
  const statusColor = STATUS_COLORS[appointment.status] || STATUS_COLORS.scheduled;

  return (
    <div style={styles.pageContainer}>
      {/* Back button */}
      <div>
        <button style={styles.backBtn} onClick={handleGoBack}>
          <BackIcon /> Back to Appointments
        </button>
      </div>

      {/* Appointment Header Card */}
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div style={styles.headerPatient}>
            <h2 style={styles.patientName}>
              {appointment.first_name || ''} {appointment.last_name || ''}
            </h2>
            {appointment.patient_phone && (
              <span style={styles.patientPhone}>{appointment.patient_phone}</span>
            )}
            {appointment.patient_email && (
              <span style={styles.patientEmail}>{appointment.patient_email}</span>
            )}
          </div>
          <span style={{
            ...styles.badge,
            backgroundColor: statusColor.bg,
            color: statusColor.text
          }}>
            {statusColor.label}
          </span>
        </div>
        <div style={styles.headerDetails}>
          <DetailItem label="Date" value={formatDate(appointment.appointment_date)} />
          <DetailItem label="Time" value={formatApptTime(appointment.start_time || appointment.appointment_date)} />
          <DetailItem label="Type" value={appointment.appointment_type || '--'} />
          <DetailItem label="Duration" value={appointment.duration_minutes ? `${appointment.duration_minutes} min` : '--'} />
          <DetailItem label="Provider" value={appointment.provider_name || '--'} />
          <DetailItem label="Department" value={appointment.department_name || '--'} />
        </div>
      </div>

      {/* Scribe Section */}
      <div style={styles.scribeSection}>
        <h3 style={styles.sectionTitle}>
          <MicIcon /> Ambient Scribe
        </h3>

        {/* Not checked in yet */}
        {!isCheckedIn && !scribeSession && (
          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              Scribe is available once the patient is checked in. Current status: <strong>{statusColor.label}</strong>
            </p>
          </div>
        )}

        {/* Ready to start */}
        {isCheckedIn && !scribeSession && (
          <div style={styles.readyBox}>
            <p style={styles.readyText}>
              Patient is checked in. Start the ambient scribe to capture the encounter.
            </p>
            <button style={styles.startBtn} onClick={handleStartScribe}>
              <MicIcon /> Start Scribe Session
            </button>
          </div>
        )}

        {/* Active scribe (processing) */}
        {hasActiveScribe && (
          <div style={styles.activeBox}>
            <p style={styles.activeText}>
              Scribe session is {scribeSession.status === 'processing' ? 'processing notes' : 'in progress'}...
            </p>
            <button style={styles.resumeBtn} onClick={() => setActiveScribe(true)}>
              {scribeSession.status === 'processing' ? 'View Progress' : 'Resume Session'}
            </button>
          </div>
        )}

        {/* Failed / cancelled — say why, allow a fresh session */}
        {hasFailedScribe && (
          <div style={styles.failedBox}>
            <p style={styles.failedText}>
              The last scribe session {scribeSession.status === 'failed' ? 'failed' : 'was cancelled'}
              {scribeSession.error_message ? `: ${scribeSession.error_message}` : '.'}
            </p>
            {isCheckedIn && (
              <button style={styles.startBtn} onClick={handleStartScribe}>
                <MicIcon /> Start New Scribe Session
              </button>
            )}
          </div>
        )}

        {/* Completed — show notes. A rejected note is a dead end without a
            way to record again (GH-184, 09-21): offer a fresh session. */}
        {hasCompletedScribe && sessionNotes?.noteStatus === 'rejected' && isCheckedIn && (
          <div style={{ ...styles.readyBox, marginBottom: 12 }}>
            <p style={styles.readyText}>These notes were rejected. You can record a new session for this visit.</p>
            <button style={styles.startBtn} onClick={handleStartScribe}>
              <MicIcon /> Start New Scribe Session
            </button>
          </div>
        )}
        {hasCompletedScribe && (
          <div>
            {notesLoading ? (
              <div style={styles.loading}>Loading clinical notes...</div>
            ) : (
              <NoteReview
                notes={sessionNotes?.notes}
                noteStatus={sessionNotes?.noteStatus}
                onSave={handleSaveNotes}
                onApprove={handleApproveNotes}
                onReject={handleRejectNotes}
                saving={notesSaving}
                onPushToEHR={handlePushToEHR}
                ehrPushStatus={sessionNotes?.ehrPushStatus}
                ehrPushError={sessionNotes?.ehrPushError}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={styles.detailItem}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  try {
    // Parse date portion directly to avoid timezone shift (midnight UTC → previous day in US timezones)
    const parts = String(dateStr).split('T')[0].split('-').map(Number);
    if (parts.length === 3 && parts[0] > 1900) {
      const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
      return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

const styles = {
  pageContainer: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    boxSizing: 'border-box'
  },
  loading: { padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' },
  errorCard: {
    textAlign: 'center', padding: '40px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
  },
  errorTitle: { margin: 0, fontSize: '18px', fontWeight: 600, color: '#991b1b' },
  errorText: { margin: 0, fontSize: '14px', color: '#6b7280' },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 14px', borderRadius: '6px',
    border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontSize: '13px',
    cursor: 'pointer', fontWeight: 500
  },
  // Header card
  headerCard: {
    backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px'
  },
  headerTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px'
  },
  headerPatient: { display: 'flex', flexDirection: 'column', gap: '2px' },
  patientName: { margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' },
  patientPhone: { fontSize: '13px', color: '#6b7280' },
  patientEmail: { fontSize: '13px', color: '#6b7280' },
  badge: { padding: '4px 14px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 },
  headerDetails: {
    display: 'flex', flexWrap: 'wrap', gap: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6'
  },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '100px' },
  detailLabel: { fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  detailValue: { fontSize: '14px', color: '#1f2937', fontWeight: 500 },
  // Scribe section
  scribeSection: {
    backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px'
  },
  sectionTitle: {
    margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: '#111827',
    display: 'flex', alignItems: 'center', gap: '8px'
  },
  infoBox: {
    padding: '16px', backgroundColor: '#f3f4f6', borderRadius: '8px'
  },
  infoText: { margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: '1.5' },
  readyBox: {
    padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
  },
  readyText: { margin: 0, fontSize: '14px', color: '#065f46', textAlign: 'center' },
  startBtn: {
    display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 24px', borderRadius: '8px',
    border: 'none', backgroundColor: '#059669', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
  },
  failedBox: {
    padding: '20px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
  },
  failedText: { margin: 0, fontSize: '14px', color: '#991b1b', textAlign: 'center', wordBreak: 'break-word' },
  activeBox: {
    padding: '20px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
  },
  activeText: { margin: 0, fontSize: '14px', color: '#1e40af', textAlign: 'center' },
  resumeBtn: {
    padding: '8px 20px', borderRadius: '6px', border: '1px solid #93c5fd',
    backgroundColor: '#fff', color: '#1e40af', fontSize: '13px', fontWeight: 500, cursor: 'pointer'
  }
};

export default AppointmentScribe;
